import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckSquare, Square, Package, CheckCircle, ArrowCounterClockwise, Plus } from '@phosphor-icons/react';
import { fmtDateFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MaterialsListTab = ({ project, isAdmin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [validating, setValidating] = useState(false);
  const [reqModal, setReqModal] = useState(null); // {name, quantity, unit}

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/materials/project/${project.id}/list`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement liste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const isOpen = data?.status === 'open';
  const readOnly = !isOpen;

  const handleUpdate = async (materialId, patch) => {
    try {
      await axios.put(`${API_URL}/api/materials/project/${project.id}/list/item/${materialId}`, patch, { withCredentials: true });
      // Optimistic update
      setData((d) => ({
        ...d,
        items: d.items.map((it) =>
          it.material_id === materialId
            ? { ...it, ...patch, updated_by_name: 'Vous', updated_at: new Date().toISOString() }
            : it,
        ),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur maj');
      load();
    }
  };

  const handleValidate = async () => {
    if (!window.confirm('Valider cette liste ? Une depense sera creee et les stocks decremenentes.')) return;
    setValidating(true);
    try {
      const res = await axios.post(`${API_URL}/api/materials/project/${project.id}/list/validate`, {}, { withCredentials: true });
      toast.success(`Liste validee : ${res.data.items_count} article(s), ${res.data.total_ht.toFixed(2)} EUR HT`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation');
    } finally {
      setValidating(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm('Ouvrir une nouvelle liste de courses pour ce chantier ?')) return;
    try {
      await axios.post(`${API_URL}/api/materials/project/${project.id}/list/reopen`, {}, { withCredentials: true });
      toast.success('Nouvelle liste ouverte');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleReqSubmit = async () => {
    if (!reqModal?.name?.trim()) { toast.error('Nom requis'); return; }
    try {
      await axios.post(`${API_URL}/api/orders`, {
        project_id: project.id,
        description: reqModal.name.trim(),
        quantity: parseInt(reqModal.quantity || 1, 10),
        unit: (reqModal.unit || 'u').trim(),
      }, { withCredentials: true });
      toast.success('Demande envoyee a l\'admin');
      setReqModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase().trim();
    if (!q) return data.items;
    return data.items.filter((i) => i.name.toLowerCase().includes(q) || (i.category || '').toLowerCase().includes(q));
  }, [data, search]);

  if (loading) return <div style={{ padding: '24px', color: '#5E5F66' }}>Chargement...</div>;
  if (!data || !data.items.length) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
        <Package size={48} style={{ marginBottom: '12px', opacity: 0.4 }} />
        <p>Aucun materiau dans le catalogue.</p>
        {isAdmin && <p style={{ fontSize: '0.85rem' }}>Ajoutez des materiaux depuis <strong>Chantiers → Bibliothèque & Catalogue → Matériaux</strong></p>}
      </div>
    );
  }

  const selectedCount = data.items.filter((i) => i.quantity > 0).length;

  return (
    <div data-testid="materials-list-tab">
      {/* Header status */}
      <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: isOpen ? '#E3F2FD' : '#E8F5E9', border: `1px solid ${isOpen ? '#90CAF9' : '#81C784'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <strong>{isOpen ? 'Liste ouverte' : 'Liste validee'}</strong>
            {!isOpen && data.validated_by && (
              <span style={{ marginLeft: '8px', color: '#5E5F66', fontSize: '0.85rem' }}>
                par {data.validated_by} le {fmtDateFR(data.validated_at)}
              </span>
            )}
            <div style={{ fontSize: '0.8rem', color: '#5E5F66' }}>{selectedCount} article(s) coche(s) avec quantite &gt; 0</div>
          </div>
          {isAdmin && isOpen && selectedCount > 0 && (
            <button data-testid="validate-list-btn" className="btn btn-primary" onClick={handleValidate} disabled={validating}>
              <CheckCircle size={16} /> {validating ? 'Validation...' : `Valider ${isAdmin && data.total_ht ? `(${data.total_ht.toFixed(2)} EUR HT)` : ''}`}
            </button>
          )}
          {isAdmin && !isOpen && (
            <button className="btn btn-secondary" onClick={handleReopen}>
              <ArrowCounterClockwise size={16} /> Ouvrir nouvelle liste
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Rechercher un materiau..."
          className="form-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px' }}
          data-testid="materials-search-input"
        />
        <button className="btn btn-secondary" data-testid="add-not-in-stock-btn"
                onClick={() => setReqModal({ name: '', quantity: 1, unit: 'u' })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={16} weight="bold" /> Ajouter (non présent)
        </button>
      </div>

      {/* Items */}
      <div style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '40px 2fr 1fr 90px 80px 110px' : '40px 1fr 100px', padding: '10px 12px', borderBottom: '1px solid #E2E2E6', background: '#F4F5F7', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#5E5F66' }}>
          <div></div>
          <div>Materiau</div>
          {isAdmin && <div>Categorie</div>}
          <div style={{ textAlign: 'right' }}>Qte</div>
          {isAdmin && <div style={{ textAlign: 'right' }}>Prix HT</div>}
          {isAdmin && <div style={{ textAlign: 'right' }}>Total HT</div>}
          {!isAdmin && <div style={{ textAlign: 'right' }}>Unite</div>}
        </div>
        {filtered.map((it) => (
          <div key={it.material_id} data-testid={`material-item-${it.material_id}`}
               style={{ display: 'grid', gridTemplateColumns: isAdmin ? '40px 2fr 1fr 90px 80px 110px' : '40px 1fr 100px', padding: '8px 12px', borderBottom: '1px solid #F4F5F7', alignItems: 'center', fontSize: '0.88rem' }}>
            <button
              onClick={() => !readOnly && handleUpdate(it.material_id, { checked: !it.checked })}
              disabled={readOnly}
              style={{ background: 'none', border: 'none', cursor: readOnly ? 'not-allowed' : 'pointer', color: it.checked ? '#002FA7' : '#5E5F66', padding: 0 }}
              data-testid={`toggle-${it.material_id}`}
            >
              {it.checked ? <CheckSquare size={22} weight="fill" /> : <Square size={22} />}
            </button>
            <div>
              <div style={{ fontWeight: '600' }}>{it.name}</div>
              {it.updated_by_name && (
                <div style={{ fontSize: '0.7rem', color: '#9A9AA0' }}>
                  Maj par {it.updated_by_name} - {fmtDateFR(it.updated_at)}
                </div>
              )}
            </div>
            {isAdmin && <div style={{ color: '#5E5F66', fontSize: '0.82rem' }}>{it.category || '-'}</div>}
            <div style={{ textAlign: 'right' }}>
              <input
                type="number"
                min="0"
                step="1"
                value={Math.round(it.quantity || 0)}
                onChange={(e) => handleUpdate(it.material_id, { quantity: e.target.value })}
                disabled={readOnly}
                data-testid={`qty-${it.material_id}`}
                style={{ width: '70px', padding: '4px 6px', border: '1px solid #E2E2E6', borderRadius: '4px', textAlign: 'right', fontSize: '0.85rem' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#5E5F66', marginLeft: '4px' }}>{it.unit}</span>
            </div>
            {isAdmin && <div style={{ textAlign: 'right', color: '#5E5F66' }}>{(it.price_ht || 0).toFixed(2)} EUR</div>}
            {isAdmin && <div style={{ textAlign: 'right', fontWeight: '600' }}>{(it.line_total_ht || 0).toFixed(2)} EUR</div>}
            {!isAdmin && <div style={{ textAlign: 'right', color: '#5E5F66', fontSize: '0.8rem' }}>{it.unit}</div>}
          </div>
        ))}
      </div>

      {isAdmin && (
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '24px', padding: '10px 14px', background: '#F4F5F7', borderRadius: '6px', fontSize: '0.9rem' }}>
          <div>Total HT : <strong>{(data.total_ht || 0).toFixed(2)} EUR</strong></div>
          <div>Total TTC : <strong>{(data.total_ttc || 0).toFixed(2)} EUR</strong></div>
        </div>
      )}

      {reqModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '480px', padding: '24px' }} data-testid="material-request-modal">
            <h3 style={{ margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><Plus size={18} weight="bold" color="#002FA7" /> Demander un matériau</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.83rem', color: '#5E5F66' }}>Ce matériau n'est pas dans le catalogue. Votre demande sera envoyée à l'admin dans l'onglet <strong>Commandes</strong>.</p>
            <div className="form-group">
              <label className="form-label">Désignation *</label>
              <input className="form-input" autoFocus value={reqModal.name} onChange={(e) => setReqModal({ ...reqModal, name: e.target.value })} placeholder="ex: Carrelage 30x30 gris"
                     data-testid="req-name-input" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Quantité *</label>
                <input type="number" min="1" step="1" className="form-input" value={reqModal.quantity}
                       onChange={(e) => setReqModal({ ...reqModal, quantity: e.target.value })} data-testid="req-qty-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Unité</label>
                <select className="form-input" value={reqModal.unit} onChange={(e) => setReqModal({ ...reqModal, unit: e.target.value })}>
                  {['u', 'ml', 'm2', 'm3', 'km', 'kg', 'sac', 'pot', 'rouleau', 'plaque', 'boite', 'palette', 'paire', 'litre'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button className="btn btn-secondary" onClick={() => setReqModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleReqSubmit} data-testid="req-submit-btn">Envoyer la demande</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsListTab;
